import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Users } from "lucide-react";
import axios from "axios";

const publicApi = axios.create({ baseURL: API });

export default function PublicTalentPool() {
    const { slug } = useParams();
    const [company, setCompany] = useState(null);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", location: "" });
    const fileRef = useRef(null);

    useEffect(() => {
        publicApi.get(`/public/talent-pool/${slug}`)
            .then((r) => setCompany(r.data))
            .catch((e) => setError(formatApiError(e)));
    }, [slug]);

    const submit = async (e) => {
        e.preventDefault();
        if (!fileRef.current?.files?.[0]) return toast.error("Please attach your resume");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name);
            fd.append("email", form.email);
            if (form.phone) fd.append("phone", form.phone);
            if (form.location) fd.append("location", form.location);
            fd.append("file", fileRef.current.files[0]);
            await publicApi.post(`/public/talent-pool/${slug}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
            setSubmitted(true);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    if (error) return <Centered title="Talent pool not available" body={error} />;
    if (!company) return <Centered title="Loading..." />;

    if (submitted) {
        return (
            <Centered title="Thanks for reaching out!">
                <p className="text-muted-foreground">We've saved your profile to <strong>{company.name}</strong>'s talent pool. They'll be in touch when a matching role opens up.</p>
            </Centered>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border bg-white">
                <div className="max-w-3xl mx-auto px-6 py-6 flex items-center gap-4">
                    {company.logo_url && <img src={company.logo_url} alt="" className="h-8" />}
                    <div>
                        <div className="text-xs uppercase tracking-widest font-mono text-muted-foreground">Talent pool</div>
                        <div className="font-display font-bold text-lg">{company.name}</div>
                    </div>
                </div>
            </header>
            <main className="max-w-3xl mx-auto px-6 py-12">
                <div className="inline-flex items-center gap-2 border border-border bg-white px-3 py-1 text-xs font-mono uppercase tracking-widest mb-6">
                    <Users className="w-3 h-3 text-ikb" /> We're not hiring right now — but tell us about you
                </div>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Join the {company.name} talent pool.</h1>
                {company.talent_pool_intro && (
                    <p className="mt-6 text-muted-foreground max-w-xl text-lg leading-relaxed">{company.talent_pool_intro}</p>
                )}

                <form onSubmit={submit} className="mt-10 border border-border bg-white p-4 sm:p-6 lg:p-8 space-y-5 max-w-xl">
                    <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-11" data-testid="tp-name" />
                    <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none h-11" data-testid="tp-email" />
                    <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none h-11" data-testid="tp-phone" />
                    <Input placeholder="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-none h-11" data-testid="tp-location" />
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Resume / CV</Label>
                        <input ref={fileRef} type="file" required accept=".pdf,.doc,.docx,.txt" className="mt-2 text-sm" data-testid="tp-file" />
                    </div>
                    <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11 px-8" data-testid="tp-submit">
                        {busy ? "Submitting..." : "Submit my profile →"}
                    </Button>
                </form>
            </main>
        </div>
    );
}

function Centered({ title, body, children }) {
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="max-w-md text-center space-y-4">
                <CheckCircle2 className="w-10 h-10 mx-auto text-ikb" />
                <h1 className="font-display font-black text-3xl">{title}</h1>
                {body && <p className="text-sm text-muted-foreground">{body}</p>}
                {children}
            </div>
        </div>
    );
}
